import React, { useEffect, useState } from "react";
import { useAuth } from "../../../context/Auth";
import { toast } from "react-toastify";
import ExportData from "../../../components/common/ExportData";
import {
  ChevronLeft,
  ChevronRight,
  Search,
  FileText,
  Calendar,
  Clock,
  Building2,
  DollarSign,
  TrendingUp,
  User,
  Package,
} from "lucide-react";

const DetailedSessionSalesReport = () => {
  const { requestWithAuth } = useAuth();
  const [date, setDate] = useState("");
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState("Consolidated");
  const [branchName, setBranchName] = useState("Consolidated");
  const [session, setSession] = useState("Consolidated");
  const [loading, setLoading] = useState(false);
  const [sessionSales, setSessionSales] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [totalSales, setTotalSales] = useState(0.0);
  const [totalItems, setTotalItems] = useState(0);

  const fetchBranches = async () => {
    try {
      const data = await requestWithAuth(
        `/company/branches/?paginated=false`,
        "GET"
      );
      setBranches(data);
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const getSessionSales = async (pageSize, search, currentPage) => {
    if (!date) {
      toast.error("Please select a date");
      return;
    }

    try {
      setLoading(true);
      const data = await requestWithAuth(
        `/report/session-sales-detail/${date}?branch=${branch}&session=${session}&search=${search}&page=${currentPage}&pageSize=${pageSize}`,
        "GET"
      );
      setSessionSales(data?.results);
      setNextPage(data?.next);
      setPrevPage(data?.prev);
      setTotalPages(Math.ceil(data?.count / pageSize));
      setTotalSales(data?.total_sales || 0);

      // Calculate total items
      const items = data?.results?.reduce(
        (sum, item) => sum + (parseInt(item?.quantity) || 0),
        0
      );
      setTotalItems(items);
    } catch (error) {
      setSessionSales([]);
      toast.error("Failed to load session sales data");
    } finally {
      setLoading(false);
    }
  };

  const getTableData = async () => {
    try {
      const data = await requestWithAuth(
        `/report/session-sales-detail/${date}?paginated=false&search=${search}&branch=${branch}&session=${session}`,
        "GET"
      );
      const formattedData = data.map((item) => ({
        "Drug Name": item?.drug,
        "Total Sales": `GHS ${parseFloat(item?.total_selling_price || 0).toFixed(
          2
        )}`,
        "Items Sold": item?.quantity,
        Cashier: item?.recorded_by,
        "User Session": item?.session,
        Date: item?.created_at,
      }));
      setTableData(formattedData);
    } catch (error) {
      setTableData([]);
    }
  };

  const applyFilters = () => {
    setCurrentPage(1);
    getSessionSales(pageSize, search, 1);
    getTableData();
  };

  const clearFilters = () => {
    setDate("");
    setBranch("Consolidated");
    setBranchName("Consolidated");
    setSession("Consolidated");
    setSearch("");
    setSessionSales([]);
    setTotalSales(0);
    setTotalItems(0);
    setCurrentPage(1);
  };

  const filterSessionSales = (search_query) => {
    setSearch(search_query);
    setCurrentPage(1);
  };

  const handleNextPage = () => {
    if (nextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (prevPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const totalNumbersToShow = 3;

    let startPage = Math.max(1, currentPage - totalNumbersToShow);
    let endPage = Math.min(totalPages, currentPage + totalNumbersToShow);

    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) {
        pageNumbers.push("...");
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }
      pageNumbers.push(totalPages);
    }

    return pageNumbers.map((number, idx) => (
      <button
        key={idx}
        onClick={() => number !== "..." && handlePageClick(number)}
        disabled={number === "..."}
        className={`min-w-[36px] h-[36px] flex items-center justify-center rounded-lg text-sm font-medium transition-all
          ${number === currentPage
            ? "bg-blue-500 text-white shadow-sm"
            : number === "..."
              ? "cursor-default text-gray-400"
              : "text-gray-600 hover:bg-gray-100"
          }`}
      >
        {number}
      </button>
    ));
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    if (date && sessionSales?.length > 0) {
      getSessionSales(pageSize, search, currentPage);
      getTableData();
    }
  }, [currentPage, pageSize]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Reports</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">
                  Detailed Session Sales Report
                </span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <FileText size={32} className="text-blue-600" />
                Detailed Session Sales Report
              </h1>
            </div>
          </div>
        </div>

        {/* Summary Cards - Only show when data is loaded */}
        {sessionSales?.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-green-100 rounded-lg">
                  <DollarSign className="text-green-600" size={20} />
                </div>
                <h3 className="text-sm font-medium text-gray-600">
                  Total Sales
                </h3>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                GHS {parseFloat(totalSales || 0).toFixed(2)}
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Clock className="text-blue-600" size={20} />
                </div>
                <h3 className="text-sm font-medium text-gray-600">Session</h3>
              </div>
              <p className="text-lg font-semibold text-gray-900 capitalize">
                {session === "Consolidated" ? "All Sessions" : session}
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Calendar className="text-purple-600" size={20} />
                </div>
                <h3 className="text-sm font-medium text-gray-600">Date</h3>
              </div>
              <p className="text-lg font-semibold text-gray-900">
                {new Date(date).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Building2 className="text-orange-600" size={20} />
                </div>
                <h3 className="text-sm font-medium text-gray-600">Branch</h3>
              </div>
              <p className="text-lg font-semibold text-gray-900">
                {branchName === "Consolidated" ? "All Branches" : branchName}
              </p>
            </div>
          </div>
        )}

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Filters Section */}
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Generate Report
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Branch Filter */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  <Building2 size={12} className="inline mr-1" />
                  Branch
                </label>
                <select
                  value={branch}
                  onChange={(e) => {
                    if (e.target.value === "Consolidated") {
                      setBranch("Consolidated");
                      setBranchName("Consolidated");
                    } else {
                      const id = Number(e.target.value);
                      const selectedBranch = branches.find((b) => b.id === id);
                      setBranch(id);
                      setBranchName(selectedBranch?.name || "");
                    }
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                >
                  <option value="Consolidated">All Branches</option>
                  {branches?.map((branchItem, index) => (
                    <React.Fragment key={index}>
                      {!branchItem?.is_head_office && !branchItem?.is_stores && (
                        <option value={branchItem.id}>{branchItem.name}</option>
                      )}
                    </React.Fragment>
                  ))}
                </select>
              </div>

              {/* Session Filter */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  <Clock size={12} className="inline mr-1" />
                  Session
                </label>
                <select
                  value={session}
                  onChange={(e) => setSession(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                >
                  <option value="Consolidated">All Sessions</option>
                  <option value="morning">Morning Session</option>
                  <option value="evening">Evening Session</option>
                </select>
              </div>

              {/* Date Selector */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  <Calendar size={12} className="inline mr-1" />
                  Date
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                />
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Clear
              </button>
              <button
                onClick={applyFilters}
                disabled={loading || !date}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${loading || !date
                  ? "bg-blue-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
                  }`}
              >
                {loading ? "Loading..." : "Generate Report"}
              </button>
            </div>
          </div>

          {/* Controls Bar - Only show when data is loaded */}
          {sessionSales?.length > 0 && (
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <ExportData
                    tableData={tableData}
                    fileName={`Detailed Session Sales - ${date} - ${branchName} - ${session}`}
                  />
                  <span className="text-sm text-gray-600">Show</span>
                  <select
                    onChange={(e) => setPageSize(e.target.value)}
                    value={pageSize}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                  <span className="text-sm text-gray-600">entries</span>
                </div>

                <div className="relative flex-1 max-w-md">
                  <Search
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    size={16}
                  />
                  <input
                    type="text"
                    placeholder="Search drugs..."
                    value={search}
                    onChange={(e) => filterSessionSales(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  <span>Loading session sales data...</span>
                </div>
              </div>
            ) : sessionSales?.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Drug Name
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Total Sales
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Items Sold
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Cashier
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      User Session
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {sessionSales?.map((item, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-4 px-6 text-sm font-medium text-gray-900">
                        {item?.drug}
                      </td>
                      <td className="py-4 px-6 text-sm font-semibold text-gray-900">
                        GHS{" "}
                        {parseFloat(item?.total_selling_price || 0).toFixed(2)}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item?.quantity}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item?.recorded_by}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600 capitalize">
                        {item?.session}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item?.created_at}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-blue-50 font-bold border-t-2 border-blue-200">
                    <td className="py-4 px-6 text-sm text-gray-900">
                      Total Sales
                    </td>
                    <td
                      colSpan={5}
                      className="py-4 px-6 text-sm text-blue-600 font-bold"
                    >
                      GHS {parseFloat(totalSales || 0).toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <div className="py-20 text-center">
                <div className="text-gray-400 mb-2">
                  <FileText className="mx-auto h-12 w-12" strokeWidth={1.5} />
                </div>
                <p className="text-gray-500 text-sm font-medium mb-1">
                  No Data Available
                </p>
                <p className="text-gray-400 text-xs">
                  Select filters and generate the report
                </p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {sessionSales?.length > 0 && (
            <div className="p-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing page {currentPage} of {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-lg transition-all ${currentPage > 1
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronLeft size={20} />
                  </button>

                  <div className="flex items-center gap-1">
                    {renderPageNumbers()}
                  </div>

                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-lg transition-all ${currentPage < totalPages
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DetailedSessionSalesReport;
